import { useState } from 'react';
import {
  Phone,
  EnvelopeSimple,
  CaretDown,
  CaretRight,
  SmileyBlank,
  Smiley,
  SmileySad,
  ArrowUp,
  ArrowDown,
  Clock,
  CheckCircle,
  Warning,
  Sparkle,
} from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type {
  ARCommunication,
  ScheduledCommunication,
} from '../../data/mockARData';

interface CommunicationsTimelineProps {
  communications: ARCommunication[];
  scheduledCommunications?: ScheduledCommunication[];
  onViewDetails?: (communication: ARCommunication) => void;
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatDateTime(dateStr);
}

const SENTIMENT_CONFIG = {
  positive: {
    icon: Smiley,
    className: 'text-green-500',
    label: 'Positive',
  },
  neutral: {
    icon: SmileyBlank,
    className: 'text-gray-500',
    label: 'Neutral',
  },
  negative: {
    icon: SmileySad,
    className: 'text-red-500',
    label: 'Negative',
  },
};

function CommunicationItem({
  communication,
  isLatest,
  onViewDetails,
}: {
  communication: ARCommunication;
  isLatest: boolean;
  onViewDetails?: (communication: ARCommunication) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(isLatest);

  const Icon = communication.type === 'email' ? EnvelopeSimple : Phone;
  const DirectionIcon =
    communication.direction === 'inbound' ? ArrowDown : ArrowUp;
  const SentimentConfig = communication.sentiment
    ? SENTIMENT_CONFIG[communication.sentiment]
    : null;

  return (
    <div className="relative pl-8 pb-6 last:pb-0">
      {/* Timeline line */}
      <div className="absolute left-3 top-6 bottom-0 w-px bg-border last:hidden" />

      {/* Timeline dot */}
      <div
        className={cn(
          'absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center',
          communication.type === 'email'
            ? 'bg-blue-100 text-blue-600'
            : 'bg-green-100 text-green-600',
        )}
      >
        <Icon className="h-3.5 w-3.5" weight="fill" />
      </div>

      {/* Content */}
      <div className="space-y-2">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 hover:text-foreground"
            >
              {isExpanded ? (
                <CaretDown className="h-3.5 w-3.5" />
              ) : (
                <CaretRight className="h-3.5 w-3.5" />
              )}
              <span className="font-medium text-sm">
                {communication.subject || communication.details}
              </span>
            </button>
            <DirectionIcon
              className={cn(
                'h-3.5 w-3.5',
                communication.direction === 'inbound'
                  ? 'text-blue-500'
                  : 'text-gray-400',
              )}
            />
            {SentimentConfig && (
              <SentimentConfig.icon
                className={cn('h-4 w-4', SentimentConfig.className)}
                weight="fill"
              />
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {formatRelativeTime(communication.date)}
          </span>
        </div>

        {/* Classifications */}
        {communication.classifications.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {communication.classifications.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Expanded content */}
        {isExpanded && (
          <div className="space-y-3 pt-2">
            {/* Summary */}
            {communication.summary && (
              <p className="text-sm text-muted-foreground">
                {communication.summary}
              </p>
            )}

            {/* Email body or transcript preview */}
            {communication.type === 'email' && communication.emailBody && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <div className="line-clamp-4 whitespace-pre-wrap">
                  {communication.emailBody}
                </div>
              </div>
            )}

            {communication.type === 'phone' && communication.transcript && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-2 max-h-40 overflow-y-auto">
                {communication.transcript.slice(0, 4).map((line, idx) => (
                  <div key={idx}>
                    <span
                      className={cn(
                        'text-xs font-semibold',
                        line.speaker === 'Customer'
                          ? 'text-blue-600'
                          : 'text-green-600',
                      )}
                    >
                      {line.speaker}:
                    </span>{' '}
                    <span className="text-muted-foreground">{line.text}</span>
                  </div>
                ))}
                {communication.transcript.length > 4 && (
                  <p className="text-xs text-muted-foreground italic">
                    ... {communication.transcript.length - 4} more messages
                  </p>
                )}
              </div>
            )}

            {/* Pending Actions / Quick Actions */}
            {communication.pendingActions &&
              communication.pendingActions.length > 0 && (
                <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-3">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
                    <Warning className="h-4 w-4" weight="fill" />
                    <span>Action Required</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {communication.pendingActions.map((action) => (
                      <div key={action.id} className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          variant={action.aiRecommended ? 'default' : 'outline'}
                          className={cn(
                            'h-8 text-xs gap-1.5',
                            action.aiRecommended &&
                              'bg-violet-600 hover:bg-violet-700',
                          )}
                        >
                          {action.aiRecommended && (
                            <Sparkle className="h-3.5 w-3.5" weight="fill" />
                          )}
                          {action.type === 'approve_grace_period' && (
                            <CheckCircle className="h-3.5 w-3.5" />
                          )}
                          {action.type === 'escalate' && (
                            <Warning className="h-3.5 w-3.5" />
                          )}
                          {action.label}
                        </Button>
                      </div>
                    ))}
                  </div>
                  {/* AI Recommendation reason */}
                  {communication.pendingActions.some(
                    (a) => a.aiRecommended,
                  ) && (
                    <div className="flex items-start gap-2 text-xs text-violet-700 bg-violet-50 rounded p-2 border border-violet-200">
                      <Sparkle
                        className="h-4 w-4 flex-shrink-0 mt-0.5"
                        weight="fill"
                      />
                      <span>
                        {
                          communication.pendingActions.find(
                            (a) => a.aiRecommended,
                          )?.aiReason
                        }
                      </span>
                    </div>
                  )}
                </div>
              )}

            {/* AI Drafted Response */}
            {communication.aiDraftedResponse && (
              <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                  <span>Drafted by AI</span>
                </div>
                <p className="text-sm whitespace-pre-wrap line-clamp-4">
                  {communication.aiDraftedResponse}
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="default" className="h-7 text-xs">
                    Send
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs">
                    Edit
                  </Button>
                </div>
              </div>
            )}

            {/* View details button */}
            {onViewDetails && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onViewDetails(communication)}
              >
                View full details
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function CommunicationsTimeline({
  communications,
  scheduledCommunications = [],
  onViewDetails,
}: CommunicationsTimelineProps) {
  // Sort communications by date descending (most recent first)
  const sortedCommunications = [...communications].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <Card>
      <Tabs defaultValue="history" className="w-full">
        <div className="px-4 pt-4">
          <TabsList>
            <TabsTrigger value="history" className="gap-1.5">
              <EnvelopeSimple className="h-4 w-4" />
              History ({communications.length})
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="gap-1.5">
              <Clock className="h-4 w-4" />
              Scheduled ({scheduledCommunications.length})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="history">
          <CardContent className="pt-4">
            {sortedCommunications.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No communications yet
              </p>
            ) : (
              <div className="space-y-0 max-h-[500px] overflow-y-auto pr-2">
                {sortedCommunications.map((comm, index) => (
                  <CommunicationItem
                    key={comm.id}
                    communication={comm}
                    isLatest={index === 0}
                    onViewDetails={onViewDetails}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </TabsContent>

        <TabsContent value="scheduled">
          <CardContent className="pt-4">
            {scheduledCommunications.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No scheduled outreach
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduledCommunications.map((sched) => (
                    <TableRow key={sched.id}>
                      <TableCell className="font-medium">
                        {formatDate(sched.scheduledDate)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {sched.type === 'email' ? (
                            <EnvelopeSimple
                              className="h-4 w-4 text-blue-600"
                              weight="fill"
                            />
                          ) : (
                            <Phone
                              className="h-4 w-4 text-green-600"
                              weight="fill"
                            />
                          )}
                          <span className="capitalize">{sched.type}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {sched.details}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
